/**
 * Data transformer utilities for the Search Projects feature.
 * Groups flat project API items by their parent UBT team, mirroring the
 * pattern used by `groupUBTItemsByTeam` in the Find & Select UBT flow.
 */

/** A single project record as returned by the search-projects API. */
export interface ProjectItem {
  /** Team identifier shared across all projects belonging to the same team. */
  id?: string | number;
  /** Original team identifier (may differ from `id` in some API responses). */
  idOrig?: string | number;
  /** Project (PIP) identifier – unique per project row. */
  pipId?: string | number;
  /** Display name of the parent UBT team. */
  UBTname?: string;
  /** Service center name associated with the project. */
  centerName?: string;
  /** Location name associated with the project. */
  locationName?: string;
  /** Name of the performance-improvement project. */
  projectName?: string;
  /** Focus area / category of the project. */
  focusArea?: string;
  /** Project results rating. */
  results?: string;
  /** Value compass ID(s) for this project. */
  valueCompassId?: string | number;
  /** Project start date string. */
  projectStartDate?: string;
}

/**
 * A single project detail entry stored inside a `GroupedProject`.
 * Contains all project-level fields that vary per project within the same team.
 */
export interface ProjectDetail {
  idOrig?: string | number;
  pipId?: string | number;
  centerName?: string;
  locationName?: string;
  projectName?: string;
  focusArea?: string;
  results?: string;
  valueCompassId?: string | number;
  projectStartDate?: string;
}

/**
 * A team-level grouping of one or more performance-improvement projects.
 * One `GroupedProject` entry is created per unique team (keyed by `id` + `UBTname`).
 */
export interface GroupedProject {
  /** Team identifier. */
  id: string;
  /** Display name of the UBT team. */
  UBTname: string;
  /** All projects belonging to this team. */
  projects: ProjectDetail[];
}

/**
 * Groups a flat list of project items by their parent UBT team.
 *
 * Items sharing the same `id` and `UBTname` are merged into a single
 * `GroupedProject` entry, with each project's detail fields stored in the
 * `projects` array.  This mirrors the `groupUBTItemsByTeam` pattern used in
 * the Find & Select UBT flow.
 *
 * @param items - Flat array of project items from the search-projects API.
 * @returns Array of team-grouped project records, preserving original order.
 */
export const groupProjectsByTeam = (items: ProjectItem[]): GroupedProject[] => {
  return items.reduce((acc: GroupedProject[], item: ProjectItem) => {
    const teamId = String(item.id ?? "");
    const teamName = String(item.UBTname ?? "");

    const existingTeam = acc.find(
      (t) => t.id === teamId && t.UBTname === teamName,
    );

    const detail: ProjectDetail = {
      idOrig: item.idOrig,
      pipId: item.pipId,
      centerName: item.centerName,
      locationName: item.locationName,
      projectName: item.projectName,
      focusArea: item.focusArea,
      results: item.results,
      valueCompassId: item.valueCompassId,
      projectStartDate: item.projectStartDate,
    };

    if (existingTeam) {
      existingTeam.projects.push(detail);
    } else {
      acc.push({
        id: teamId,
        UBTname: teamName,
        projects: [detail],
      });
    }

    return acc;
  }, []);
};
